import { CfiFormResult } from "./cfiTypes";

type FormErrors = Record<string, any>;

const requiredTextError = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0
    ? undefined
    : "This field is required";

function childErrors(child: Partial<CfiFormResult["children"][number]>) {
  const errors: FormErrors = {};

  for (const field of ["name", "dateOfBirth", "nihb"] as const) {
    const error = requiredTextError(child[field]);
    if (error) errors[field] = error;
  }

  return errors;
}

function itemErrors(
  item: Partial<NonNullable<CfiFormResult["health"]>[number]>,
) {
  const errors: FormErrors = {};

  for (const field of ["name", "reason", "description"] as const) {
    const error = requiredTextError(item[field]);
    if (error) errors[field] = error;
  }

  if (
    item.professionalRecommendation !== "Yes" &&
    item.professionalRecommendation !== "No"
  ) {
    errors.professionalRecommendation = "Choose Yes or No";
  }

  return errors;
}

export function validateCfiForm(values: Partial<CfiFormResult>): FormErrors {
  const errors: FormErrors = {};
  const children = values.children ?? [];

  if (!Array.isArray(children) || children.length === 0) {
    errors.children = "At least one child is required";
  } else {
    const childFieldErrors = children.map(childErrors);
    if (
      childFieldErrors.some((childError) => Object.keys(childError).length > 0)
    ) {
      errors.children = childFieldErrors;
    }
  }

  const categories = [values.health, values.education, values.socialSupport];
  if (
    !categories.some(
      (category) => Array.isArray(category) && category.length > 0,
    )
  ) {
    errors.requestedItems = "Select at least one category";
  }

  for (const category of ["health", "education", "socialSupport"] as const) {
    const items = values[category];
    if (!Array.isArray(items)) continue;

    const itemFieldErrors = items.map(itemErrors);
    if (
      itemFieldErrors.some((itemError) => Object.keys(itemError).length > 0)
    ) {
      errors[category] = itemFieldErrors;
    }
  }

  if (
    values.hasPendingApplications !== "Yes" &&
    values.hasPendingApplications !== "No"
  ) {
    errors.hasPendingApplications = "Choose Yes or No";
  }

  for (const field of [
    "includedAllInformation",
    "pendingApplicationsDisclosed",
    "authorizeSharing",
    "informationTrue",
  ] as const) {
    if (values[field] !== true) errors[field] = "This must be checked";
  }

  for (const field of ["fullName", "dateSigned"] as const) {
    const error = requiredTextError(values[field]);
    if (error) errors[field] = error;
  }

  return errors;
}
